import React, { useEffect, useState } from 'react';
import {
  BORDER, TAN, DocTitle, Panel, Field, CheckBox, SignatureField, REQ, V, getError, today,
} from './ui';

const SummaryLine = ({ label, value, className = '' }) => (
  <div className={`flex items-baseline gap-2 border-b border-gray-300 pb-1 ${className}`}>
    <span className="text-[13px] font-semibold text-gray-800 whitespace-nowrap">{label}</span>
    <span className="min-w-0 flex-1 truncate text-sm text-gray-700">{value || '—'}</span>
  </div>
);

/** Page 4 of the Word document – "AUTHORIZATION AGREEMENT FOR PREAUTHORIZED DEBITS" */
const StepAgreement = ({ register, errors, watch, setValue }) => {
  const [show, setShow] = useState(false);
  // pre-fill today's date (like the subscription agreement) if it is empty
  const dateValue = watch('agreement.date');
  useEffect(() => {
    if (!dateValue) setValue('agreement.date', today(), { shouldDirty: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const c = watch('companyInfo') || {};
  const f = { register, errors };
  const agreeError = getError(errors, 'agreement.isSigned');

  return (
    <div>
      <DocTitle>Authorization Agreement for Preauthorized Debits</DocTitle>

      {/* company block (read-only, pre-filled from step 1) */}
      <Panel className="grid grid-cols-1 gap-x-8 gap-y-3 bg-[#faf9f7] p-4 md:grid-cols-12 md:p-5">
        <SummaryLine label="Legal Business Name:" value={c.legalName} className="md:col-span-8" />
        <SummaryLine label="EIN:" value={c.ein} className="md:col-span-4" />
        <SummaryLine label="Trade Name (if applicable):" value={c.dbaName} className="md:col-span-12" />
        <SummaryLine label="Business Address:" value={c.address} className="md:col-span-12" />
        <SummaryLine label="City:" value={c.city} className="md:col-span-5" />
        <SummaryLine label="State:" value={c.state} className="md:col-span-3" />
        <SummaryLine label="Zip Code:" value={c.zip} className="md:col-span-4" />
      </Panel>

      {/* legal text */}
      <div className={`mt-6 max-h-[22rem] space-y-3 overflow-y-auto border ${BORDER} p-4 text-[13px] leading-relaxed text-gray-700 md:p-5`}>
        <p>
          I (we) hereby authorize <strong>Everee, Inc.</strong> or its agent or assignee, hereinafter called “Company” to initiate
          pre-authorized ACH debits (“PADs”) for payroll, payment, human resource services, loan repayments, interest payments and
          any applicable fees, in accordance with the service agreement(s), to the account listed below. This pre-authorized debit
          agreement (“PAD Agreement”) is for Business PADs which are of a variable amount. PADs for interest and any applicable fees
          are due according to your contract with Company and PADs for loan repayments will occur at the end of your specific
          repayment term.
        </p>
        <p>
          You have waived your right to receive pre-notification of the amount of the PAD and agreed that you do not require advance
          notice of the amount of PADs before the debits are processed.
        </p>
        <p>
          You have certain recourse rights if any debit does not comply with this PAD Agreement. For example, you have the right to
          receive reimbursement for any PAD that is not authorized or is not consistent with this PAD Agreement. To obtain more
          information about your recourse rights, you can contact your financial institution.
        </p>
        <p>
          This authorization applies not only to the account listed, but also to any updates of this account in Everee, by an
          individual with administrative access, as granted by you. This authorization is to remain in full force and effect until
          the Company has received written notification of its termination in such time and in such manner as to afford the Company
          a reasonable opportunity to act on it.
        </p>
        <p className="rounded bg-amber-50 px-3 py-2 font-medium text-amber-900">
          Insufficient funds in your account will result in fee equal to 2% of the total amount owed AND your payroll will not be
          processed on time. Please remember to maintain adequate balances in your account to avoid any additional fees.
        </p>
      </div>

      {/* bank details */}
      <div className={`mt-6 border ${BORDER}`}>
        <Field {...f} layout="row" name="agreement.bankDetails.institutionName" label="Financial Institution Name:" rules={{ required: REQ }} />
        <Field {...f} layout="row" name="agreement.bankDetails.routingNumber" label="Transit / ABA Number:" mask="aba" placeholder="9 digits" rules={{ required: REQ, validate: V.aba }} />
        <div className="relative">
          <Field
            {...f}
            layout="row"
            name="agreement.bankDetails.accountNumber"
            label="Account #:"
            type={show ? 'text' : 'password'}
            inputMode="numeric"
            autoComplete="off"
            rules={{ required: REQ, minLength: { value: 4, message: 'Too short' } }}
          />
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="absolute right-3 top-2 text-xs font-semibold text-blue-700 hover:underline"
          >
            {show ? 'Hide' : 'Show'}
          </button>
        </div>
      </div>

      {/* signature */}
      <div className={`mt-6 border ${BORDER}`}>
        <div className={`${TAN} px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-gray-700`}>Authorized signer</div>
        <div className="grid grid-cols-1 gap-x-8 gap-y-4 p-4 md:grid-cols-2 md:p-5">
          <SignatureField {...f} name="agreement.signature" className="md:col-span-2" />
          <Field {...f} name="agreement.signerName" label="Print Name:" rules={{ required: REQ }} />
          <Field {...f} name="agreement.signerTitle" label="Title:" rules={{ required: REQ }} />
          <Field {...f} name="agreement.date" label="Date:" type="date" rules={{ required: REQ }} />
        </div>
      </div>

      <label className={`mt-6 flex cursor-pointer items-start gap-3 rounded border p-4 ${agreeError ? 'border-red-400 bg-red-50' : 'border-blue-200 bg-blue-50/60'}`}>
        <CheckBox className="mt-0.5 !h-5 !w-5" {...register('agreement.isSigned', { required: 'You must agree to proceed' })} />
        <span className="text-sm font-medium text-gray-800">
          I agree to the Terms and Conditions. I certify that I am an authorized signer and authorize ACH debits for the account
          provided.
        </span>
      </label>
      {agreeError && <p role="alert" className="mt-1 text-xs text-red-600">{agreeError.message}</p>}
    </div>
  );
};

export default StepAgreement;